import React, { FC } from 'react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';

import Column from 'components/common/Column';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import CommonImage from 'components/common/CommonImage';
import { ICON_COPY_LIGHT_GRAY } from 'assets/icons';
import {
  Delivery,
  DeliveryState,
  TransactionState,
  TransactionType,
} from 'types/api';
import { DELIVERY_STATE_LIST } from './FilterContainer';
import { formatPhoneNumber } from 'utils/formatting';

interface Props {
  deliveryItem: Delivery;
  isMobile: boolean;
  isEqual: boolean;
  handleSelectDelivery: (delivery: Delivery) => void;
}

const DeliveryCard: FC<Props> = props => {
  const { deliveryItem, isMobile, isEqual, handleSelectDelivery } = props;

  const isCancelDelivery = deliveryItem.order.items.every(
    item =>
      !isEmpty(item.dependedItems) &&
      item.dependedItems.every(
        dependItem =>
          dependItem.transaction.status === TransactionState.REQUESTED &&
          dependItem.transaction.type === TransactionType.CANCELLATION,
      ),
  );

  const optionCount = deliveryItem.items.reduce(
    (sum, item) => sum + item.count,
    0,
  );

  const deliveryState = DELIVERY_STATE_LIST.find(
    item => item.value === deliveryItem.status,
  );

  const deliveryPrice = deliveryItem.items.reduce(
    (sum, item) => Number(sum) + Number(item.info.price.sale) * item.count,
    0,
  );

  return (
    <Row
      justifyContent="space-between"
      gap="24px"
      style={{
        padding: isMobile ? '16px 20px' : '16px 40px',
        width: '100%',
        backgroundColor: isEqual && !isMobile ? '#F4F4F5' : '#fff',
        cursor: 'pointer',
      }}
      onClick={() => handleSelectDelivery(deliveryItem)}
    >
      <Column align="flex-start" gap="6px" style={{ width: 'auto' }}>
        <Text color="#46474C" size="16px" weight={600}>
          {deliveryItem.items[0].info.name}{' '}
          {optionCount > 1 && `외 ${optionCount - 1}개`}
        </Text>
        <Column align="flex-start" gap="8px">
          <Text color="#70737c" size="13px" weight={400}>
            {deliveryItem.items[0].info.product.title}
          </Text>
          <Row
            gap="8px"
            justifyContent="flex-start"
            style={{ flexWrap: 'wrap' }}
          >
            <Text
              color="#70737C"
              style={
                deliveryItem.customer.nickname.length > 10 && {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '90px',
                }
              }
            >
              {deliveryItem.customer.nickname}
            </Text>
            <Text color="#70737C">•</Text>
            <Row>
              <Text color="#70737C">
                {formatPhoneNumber(deliveryItem.customer.mobileNumber)}
              </Text>
              <Row
                onClick={e => {
                  e.stopPropagation();

                  navigator.clipboard.writeText(
                    deliveryItem.customer.mobileNumber,
                  );

                  window.showToast('연락처가 복사되었습니다.', 'success', 3000);
                }}
              >
                <CommonImage
                  src={ICON_COPY_LIGHT_GRAY}
                  style={{ marginLeft: '4px' }}
                />
                <Text size="10px" weight={400}>
                  복사
                </Text>
              </Row>
            </Row>
          </Row>
          <Text color="#989BA2" size="12px" weight={400}>
            {dayjs(deliveryItem.createdAt).format('YYYY년 MM월 DD일 HH:mm')}
          </Text>
        </Column>
      </Column>
      <Column align="flex-end" gap="8px" style={{ width: 'auto' }}>
        {isCancelDelivery ? (
          <Text
            color="#FF4242"
            size="11px"
            weight={600}
            style={{ minWidth: '60px' }}
          >
            주문 취소 요청
          </Text>
        ) : (
          <Text color={deliveryState.color} size="11px" weight={600}>
            {deliveryState.name}
          </Text>
        )}
        <Text
          color="#1b1c1e"
          size="18px"
          weight={600}
          style={{
            lineHeight: '26px',
            textWrap: 'nowrap',
            textDecoration:
              deliveryItem.status === DeliveryState.CANCELED
                ? 'line-through'
                : 'none',
          }}
        >
          {deliveryPrice.toLocaleString()}원
        </Text>
      </Column>
    </Row>
  );
};

export default DeliveryCard;
